import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Lock,
    Unlock,
    Eye,
    Edit,
    Trash2,
    Plus,
    Search,
    Filter,
    Shield,
    FileText,
    Database,
    Users,
    Settings,
    Bell,
    Globe,
    Key
} from "lucide-react";
import BackButton from "../../components/BackButton";

const ManagePermissions = () => {
    const [permissions, setPermissions] = useState([
        {
            id: 1,
            name: "View Projects",
            description: "Allow viewing project details and progress",
            category: "Projects",
            granted: true,
            icon: <Eye className="w-5 h-5" />
        },
        {
            id: 2,
            name: "Edit Projects",
            description: "Allow editing project information and settings",
            category: "Projects",
            granted: false,
            icon: <Edit className="w-5 h-5" />
        },
        {
            id: 3,
            name: "Delete Projects",
            description: "Allow deleting projects and associated data",
            category: "Projects",
            granted: false,
            icon: <Trash2 className="w-5 h-5" />
        },
        {
            id: 4,
            name: "Manage Teams",
            description: "Allow team management and member assignments",
            category: "Teams",
            granted: true,
            icon: <Users className="w-5 h-5" />
        },
        {
            id: 5,
            name: "System Configuration",
            description: "Allow access to system settings and configuration",
            category: "System",
            granted: false,
            icon: <Settings className="w-5 h-5" />
        },
        {
            id: 6,
            name: "View Analytics",
            description: "Allow viewing system analytics and reports",
            category: "Analytics",
            granted: true,
            icon: <Database className="w-5 h-5" />
        },
        {
            id: 7,
            name: "Manage Users",
            description: "Allow user account management and role assignment",
            category: "Users",
            granted: false,
            icon: <Users className="w-5 h-5" />
        },
        {
            id: 8,
            name: "Global Settings",
            description: "Allow modifying global application settings",
            category: "System",
            granted: false,
            icon: <Globe className="w-5 h-5" />
        }
    ]);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [newPermission, setNewPermission] = useState({ name: "", description: "", category: "Projects" });
    const [showAddForm, setShowAddForm] = useState(false);

    const categories = ["All", "Projects", "Teams", "System", "Analytics", "Users"];

    const togglePermission = (id) => {
        setPermissions(prev =>
            prev.map(perm =>
                perm.id === id ? { ...perm, granted: !perm.granted } : perm
            )
        );
    };

    const addPermission = () => {
        if (newPermission.name.trim() && newPermission.description.trim()) {
            const newPerm = {
                id: permissions.length + 1,
                name: newPermission.name,
                description: newPermission.description,
                category: newPermission.category,
                granted: false,
                icon: <Key className="w-5 h-5" />
            };
            setPermissions([...permissions, newPerm]);
            setNewPermission({ name: "", description: "", category: "Projects" });
            setShowAddForm(false);
        }
    };

    const filteredPermissions = permissions.filter(perm => {
        const matchesSearch = perm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            perm.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === "All" || perm.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const categoryStats = categories.filter(cat => cat !== "All").map(category => ({
        name: category,
        count: permissions.filter(p => p.category === category).length,
        granted: permissions.filter(p => p.category === category && p.granted).length
    }));

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <BackButton to="/admin/dashboard" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Manage Permissions</h1>
                                <p className="text-gray-600 mt-1">Control access permissions for different roles</p>
                            </div>
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setShowAddForm(true)}
                            className="btn-primary flex items-center"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Add Permission
                        </motion.button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 py-8">
                {/* Stats Overview */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Permissions</p>
                                    <p className="text-2xl font-bold text-gray-900">{permissions.length}</p>
                                </div>
                                <Shield className="w-8 h-8 text-primary-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Active Permissions</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {permissions.filter(p => p.granted).length}
                                    </p>
                                </div>
                                <Lock className="w-8 h-8 text-green-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Categories</p>
                                    <p className="text-2xl font-bold text-gray-900">{categories.length - 1}</p>
                                </div>
                                <FileText className="w-8 h-8 text-blue-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Restricted</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {permissions.filter(p => !p.granted).length}
                                    </p>
                                </div>
                                <Unlock className="w-8 h-8 text-red-600" />
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Search and Filter */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search permissions..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input-field pl-10"
                            />
                        </div>
                        <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
                            {categories.map(category => (
                                <button
                                    key={category}
                                    onClick={() => setSelectedCategory(category)}
                                    className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                                        selectedCategory === category
                                            ? 'bg-primary-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Add Permission Form */}
                {showAddForm && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card mb-8"
                    >
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">Add New Permission</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Permission Name
                                </label>
                                <input
                                    type="text"
                                    value={newPermission.name}
                                    placeholder="e.g., Manage Users"
                                    onChange={(e) => setNewPermission({...newPermission, name: e.target.value})}
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Category
                                </label>
                                <select
                                    value={newPermission.category}
                                    onChange={(e) => setNewPermission({...newPermission, category: e.target.value})}
                                    className="input-field"
                                >
                                    {categories.filter(c => c !== "All").map(category => (
                                        <option key={category} value={category}>{category}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={newPermission.description}
                                    placeholder="Describe what this permission allows..."
                                    onChange={(e) => setNewPermission({...newPermission, description: e.target.value})}
                                    className="input-field min-h-[100px] resize-none"
                                    rows={3}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowAddForm(false);
                                    setNewPermission({ name: "", description: "", category: "Projects" });
                                }}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addPermission}
                                className="btn-primary"
                            >
                                Add Permission
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* Permissions Grid */}
                <div className="grid gap-4">
                    {filteredPermissions.map((perm, index) => (
                        <motion.div
                            key={perm.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="card hover:shadow-medium"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-start space-x-4 flex-1">
                                    <div className={`p-3 rounded-xl ${
                                        perm.granted ? 'bg-green-100' : 'bg-gray-100'
                                    }`}>
                                        <div className={perm.granted ? 'text-green-600' : 'text-gray-400'}>
                                            {perm.icon}
                                        </div>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-3 mb-1">
                                            <h3 className="text-lg font-semibold text-gray-900">{perm.name}</h3>
                                            <span className={`badge ${
                                                perm.category === 'Projects' ? 'badge-info' :
                                                    perm.category === 'Teams' ? 'badge-success' :
                                                        perm.category === 'System' ? 'badge-warning' :
                                                            'badge-error'
                                            }`}>
                        {perm.category}
                      </span>
                                        </div>
                                        <p className="text-gray-600 text-sm mb-3">{perm.description}</p>
                                        <div className="flex items-center space-x-4">
                      <span className={`text-sm font-medium ${
                          perm.granted ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {perm.granted ? 'Active' : 'Restricted'}
                      </span>
                                            <span className="text-sm text-gray-500">
                        ID: PERM-{perm.id.toString().padStart(3, '0')}
                      </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={() => togglePermission(perm.id)}
                                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                                            perm.granted
                                                ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                                : 'bg-green-50 text-green-700 hover:bg-green-100'
                                        }`}
                                    >
                                        {perm.granted ? (
                                            <>
                                                <Lock className="w-4 h-4" />
                                                <span>Revoke</span>
                                            </>
                                        ) : (
                                            <>
                                                <Unlock className="w-4 h-4" />
                                                <span>Grant</span>
                                            </>
                                        )}
                                    </button>

                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={perm.granted}
                                            onChange={() => togglePermission(perm.id)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    </label>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Empty State */}
                {filteredPermissions.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-16"
                    >
                        <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                            <Lock className="w-12 h-12 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No permissions found</h3>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto">
                            {searchTerm || selectedCategory !== "All"
                                ? "No permissions match your search criteria. Try adjusting your filters."
                                : "Start by creating permissions to control access to different features."
                            }
                        </p>
                        {(searchTerm || selectedCategory !== "All") ? (
                            <button
                                onClick={() => {
                                    setSearchTerm("");
                                    setSelectedCategory("All");
                                }}
                                className="btn-primary"
                            >
                                Clear Filters
                            </button>
                        ) : (
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="btn-primary"
                            >
                                <Plus className="w-5 h-5 mr-2" />
                                Create Your First Permission
                            </button>
                        )}
                    </motion.div>
                )}

                {/* Category Breakdown */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-12"
                >
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Permission Categories</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {categoryStats.map((category, index) => (
                            <motion.div
                                key={category.name}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="card"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                                    <div className="text-2xl font-bold text-gray-900">{category.count}</div>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Active Permissions</span>
                                        <span className="font-medium text-green-600">{category.granted}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Restricted Permissions</span>
                                        <span className="font-medium text-red-600">{category.count - category.granted}</span>
                                    </div>
                                    <div className="pt-3">
                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
                                                style={{ width: `${(category.granted / category.count) * 100}%` }}
                                            />
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-500 mt-2">
                                            <span>{Math.round((category.granted / category.count) * 100)}% Active</span>
                                            <span>{category.count} Total</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ManagePermissions;